import type { FC } from "react";
import { useEffect } from "react";

import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { SURVEY_ELIGIBLE_ORDER_STATUSES } from "@jti/commerce/shared";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { BrowserStorage } from "@jti/rest-api";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";
import { SobQuiz, Survey } from "@jti/ui";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";

import { useGtmOrderSummaryPurchase } from "@checkout-v2/shared";
import { useCustomerOrder } from "@checkout-v2/shared/hooks/useCustomerOrder";
import { useSurveyScriptUrl } from "@checkout-v2/shared/hooks/useSurveyScriptUrl";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";

import { AdditionalInfo } from "./components/AdditionalInfo/AdditionalInfo";
import { ConsentReminder } from "./components/ConsentReminder";
import { GoToOrderButton } from "./components/GoToOrderButton";
import { OrderLoyaltyConfirmationSection } from "./components/OrderLoyaltyConfirmationSection";
import { OrderSection } from "./components/OrderSection";
import { SocialMedia } from "./components/SocialMedia/SocialMedia";
import { SuccessMessage } from "./components/SuccessMessage/SuccessMessage";

import styles from "./ThankYouPage.module.scss";

const { sanitize } = dompurify;

export const ThankYouPage: FC = () => {
  const { showQuizOnThankYouPage } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const authEntryHref = resolveAuthHref({ route: AUTH_ROUTE_PATHS.entry });
  useGtmOrderSummaryPurchase();

  const { survey, sob, orderSummaryConfig } = useCheckoutV2Configuration();

  const surveyScriptUrl = useSurveyScriptUrl(survey?.identifier, survey?.domain);

  const { data: order, isLoading, isSuccess } = useCustomerOrder();

  const { contactEmail } = useGetProfileQuery(undefined, {
    selectFromResult: ({ data }) => ({
      contactEmail: data?.personalInfo.email,
    }),
  });

  const orderFetchStatus = isLoading ? "pending" : isSuccess ? "success" : "error";

  const { socialMedia, displaySocialMedia, socialMediaTitle } = orderSummaryConfig || {};
  const shouldDisplaySocialMedia = !!(displaySocialMedia && socialMedia && socialMedia.length > 0);

  const { displayAdditionalInfo: shouldDisplayAdditionalInfo, additionalInfo } = orderSummaryConfig || {};

  const isOrderEligibleForSurvey =
    isSuccess && !!order?.number && !!order?.status && SURVEY_ELIGIBLE_ORDER_STATUSES.includes(order.status);

  const shouldDisplaySurvey = showQuizOnThankYouPage && !!surveyScriptUrl && isOrderEligibleForSurvey;
  const shouldDisplaySobQuiz = showQuizOnThankYouPage && !surveyScriptUrl && !!sob?.enabled && isOrderEligibleForSurvey;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!new BrowserStorage().get("user", null)) {
      window.location.href = authEntryHref;
    }
  }, [authEntryHref]);

  return (
    <>
      <ConsentReminder />
      <div className={styles.container}>
        <SuccessMessage status={orderFetchStatus} email={contactEmail} />
        <GoToOrderButton orderId={order?.number} />

        {shouldDisplaySurvey && !!surveyScriptUrl && <Survey scriptUrl={surveyScriptUrl} />}
        {shouldDisplaySobQuiz && !!sob.data && Object.keys(sob.data).length > 0 && <SobQuiz data={sob.data} />}

        <OrderSection />

        <OrderLoyaltyConfirmationSection />

        {shouldDisplayAdditionalInfo && <AdditionalInfo additionalInfo={additionalInfo} />}
        {!!orderSummaryConfig?.withdrawOrderInfo && (
          <div
            className={styles.withdrawOrderInfo}
            dangerouslySetInnerHTML={{ __html: sanitize(orderSummaryConfig.withdrawOrderInfo) }}
          />
        )}
        {shouldDisplaySocialMedia && <SocialMedia socialMediaTitle={socialMediaTitle} socialMedia={socialMedia} />}
      </div>
    </>
  );
};
